import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../utils/api-client';

export type EntityType = 'location' | 'unit' | 'contract';

export type EntityAdmin = {
  registrantid: string;
  name?: string;
  fname?: string | null;
  lname?: string | null;
  reltype?: string;
  registrantidentity?: string;
};

export const ENTITY_ADMINS_QUERY_KEY = 'entityAdmins';
export const ENTITY_MEMBERS_QUERY_KEY = 'entityMembers';

interface EntityQueryParams {
  entityId: string;
  entityType: EntityType;
  searchQuery?: string;
  page?: number;
  limit?: number;
}

const getEntityEndpoint = (entityType: EntityType, entityId: string) => {
  switch (entityType) {
    case 'location':
      return `/api/locations/${entityId}`;
    case 'unit':
      return `/api/units/${entityId}`;
    case 'contract':
      return `/api/contracts/${entityId}`;
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
};

const getEntityAdmins = async ({
  entityId,
  entityType,
  searchQuery = '',
  page = 1,
  limit = 10,
}: EntityQueryParams): Promise<{
  admins: EntityAdmin[];
  totalPages: number;
}> => {
  if (!entityId) {
    return { admins: [], totalPages: 0 };
  }

  const endpoint = getEntityEndpoint(entityType, entityId);
  const response = await fetch(`${endpoint}/admins?q=${searchQuery}&page=${page}&limit=${limit}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${entityType} admins`);
  }

  return response.json();
};

const getEntityMembers = async ({
  entityId,
  entityType,
  searchQuery = '',
  page = 1,
  limit = 10,
}: EntityQueryParams): Promise<{
  members: EntityAdmin[];
  totalPages: number;
}> => {
  if (!entityId) {
    return { members: [], totalPages: 0 };
  }

  const endpoint = getEntityEndpoint(entityType, entityId);
  const response = await fetch(`${endpoint}/members?q=${searchQuery}&page=${page}&limit=${limit}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${entityType} members`);
  }

  return response.json();
};

export const useEntityAdmins = ({
  entityId,
  entityType,
  searchQuery = '',
  page = 1,
  limit = 10,
}: EntityQueryParams) => {
  return useQuery({
    queryKey: [ENTITY_ADMINS_QUERY_KEY, entityType, entityId, searchQuery, page, limit],
    queryFn: () => getEntityAdmins({ entityId, entityType, searchQuery, page, limit }),
    placeholderData: keepPreviousData,
    enabled: !!entityId,
  });
};

export const useEntityMembers = ({
  entityId,
  entityType,
  searchQuery = '',
  page = 1,
  limit = 10,
}: EntityQueryParams) => {
  return useQuery({
    queryKey: [ENTITY_MEMBERS_QUERY_KEY, entityType, entityId, searchQuery, page, limit],
    queryFn: () => getEntityMembers({ entityId, entityType, searchQuery, page, limit }),
    placeholderData: keepPreviousData,
    enabled: !!entityId,
  });
};

export const usePromoteToEntityAdmin = (
  entityId: string,
  entityType: EntityType,
  options?: { onSuccess?: () => void },
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (registrantId: string) => {
      const endpoint = getEntityEndpoint(entityType, entityId);
      const response = await apiFetch(`${endpoint}/admins`, {
        method: 'POST',
        body: JSON.stringify({ registrantid: registrantId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to promote member to ${entityType} admin`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_ADMINS_QUERY_KEY, entityType, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: [ENTITY_MEMBERS_QUERY_KEY, entityType, entityId],
      });
      options?.onSuccess?.();
    },
  });
};

export const useDemoteToEntityMember = (
  entityId: string,
  entityType: EntityType,
  options?: { onSuccess?: () => void },
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (registrantId: string) => {
      const endpoint = getEntityEndpoint(entityType, entityId);
      const response = await apiFetch(`${endpoint}/admins/${registrantId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to demote admin to member`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_ADMINS_QUERY_KEY, entityType, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: [ENTITY_MEMBERS_QUERY_KEY, entityType, entityId],
      });
      options?.onSuccess?.();
    },
  });
};

export const getEntityUserName = (admin: EntityAdmin): string => {
  if (admin.name) {
    return admin.name;
  }

  if (admin.fname || admin.lname) {
    return `${admin.fname || 'Unknown'} ${admin.lname || 'User'}`;
  }

  return 'Unknown User';
};
